import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import GeometryLayer, { GeometryMode } from "plugins/shared/layers/GeometryLayer.class"
import { RulerMidpointTextLayer } from "./layers/RulerMidpointText"
import { RulerAreaTextLayer } from "./layers/RulerAreaText"
import { useEffect, useMemo, useRef } from "react"
import { rulerFillColor, rulerOutlineColor } from "@nova/constants"
import {
    selectRulersToDraw,
    selectRulersWithMockToDraw,
} from "features/entity-caching/rulers/selectors"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import { clearEditingData, MOCKED_RULER_ID, selectRulerEditingData } from "./slice"
import { arePolygonsEqual } from "@nova/calculate-geospatial"
import { useNonReactiveCallback } from "@nova/use-non-reactive-callback"
import { selectRulerTemplate } from "../tool-registry"
import { GeometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { createRuler, deleteRuler, updateRuler } from "features/pubsub/entityActions/cubes/ruler"
import { selectUserID } from "user/profile/selectors"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

export const useNormalRulerLayer = () => {
    const rulers = useAppSelector(selectRulersToDraw)
    const editingData = useAppSelector(selectRulerEditingData)
    const filteredRulers = useMemo(() => {
        return rulers
            .filter((e) => e.id !== editingData?.id)
            .map((e, i) => ({ ...e, deckGlIdx: i, idx: i }))
    }, [rulers, editingData])

    const snapshotIsOpen = useAppSelector(selectSnapshotOpen())

    const outlineColor = convertHexStringToRGBArray(rulerOutlineColor)
    const fillColor = convertHexStringToRGBArray(rulerFillColor)

    if (snapshotIsOpen) return []
    if (filteredRulers.length === 0) return []

    return new GeometryLayer({
        id: `ruler-geometry`,
        data: filteredRulers,
        visible: true,
        extensions: [...getGlobalTerrainData().extensions],
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: outlineColor,
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineColor: [255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getFillColor: outlineColor,
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: outlineColor,
            },
            lines: {
                widthScale: 3,
                widthMaxPixels: 3,
                widthMinPixels: 3,
                getColor: outlineColor,
                getWidth: 4,
                capRounded: false,
                billboard: false,
                miterLimit: 0,
            },
            polygons: {
                getFillColor: fillColor,
                getLineColor: outlineColor,
                stroked: true,
                getLineWidth: 3,
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
            },
        },
        extraLayers: [
            {
                provider: "polygons",
                layer: RulerMidpointTextLayer,
            },
            {
                provider: "polygons",
                layer: RulerAreaTextLayer,
            },
        ],
    })
}

export const useEditingRulerLayer = () => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)!
    const editingData = useAppSelector(selectRulerEditingData)
    const allRulers = useAppSelector(selectRulersWithMockToDraw)
    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const workspaceAreaUnit = useAppSelector(selectWorkspaceAreaUnit)
    const filteredRulers = useMemo(() => {
        return editingData
            ? [{ ...editingData.mockedRuler, deckGlIdx: allRulers.length - 1, idx: 0 }]
            : []
    }, [editingData, allRulers.length])

    const snapshotIsOpen = useAppSelector(selectSnapshotOpen())

    const outlineColor = convertHexStringToRGBArray(rulerOutlineColor)
    const fillColor = convertHexStringToRGBArray(rulerFillColor)

    const rulerTemplate = useAppSelector(selectRulerTemplate)
    const rulerTemplateRef = useRef(rulerTemplate)
    useEffect(() => {
        rulerTemplateRef.current = rulerTemplate
    }, [rulerTemplate])

    const flush = useNonReactiveCallback(
        (store: GeometryDataStore, save: boolean) => {
            if (save) {
                if (editingData?.id === MOCKED_RULER_ID && store.polygon.coordinates.length >= 2) {
                    const distanceUnit =
                        rulerTemplateRef.current.distanceUnit || workspaceDistanceUnit
                    const areaUnit = rulerTemplateRef.current.areaUnit || workspaceAreaUnit

                    dispatch(
                        createRuler({
                            data: {
                                coordinates: store.polygon.coordinates,
                                geometryType: store.polygon.geometryType,
                                createdBy: { userId },
                                distanceUnit,
                                areaUnit,
                            },
                        })
                    )
                } else if (editingData && editingData.id !== MOCKED_RULER_ID) {
                    const coordinates = store.polygon.coordinates

                    if (coordinates.length < 2) {
                        dispatch(deleteRuler({ id: editingData.id }))
                    } else if (
                        editingData.mockedRuler.geometryType !== store.polygon.geometryType ||
                        !arePolygonsEqual([editingData.mockedRuler], [{ coordinates }])
                    ) {
                        dispatch(
                            updateRuler({
                                id: editingData.id,
                                updates: { coordinates, geometryType: store.polygon.geometryType },
                            })
                        )
                    }
                }
            }
            dispatch(clearEditingData())
        },
        [editingData]
    )

    if (snapshotIsOpen || filteredRulers.length === 0) return []

    return new GeometryLayer({
        id: `ruler-geometry-editing`,
        data: filteredRulers,
        visible: true,
        editData: {
            canEdit: true,
            flush,
        },
        drawData: {
            canDraw: true,
            drawMode: "points",
        },
        geometryName: "Measure",
        mode: GeometryMode.LineToPolygon,
        extensions: [...getGlobalTerrainData().extensions],
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: outlineColor,
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineColor: [255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getFillColor: outlineColor,
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: outlineColor,
            },
            lines: {
                widthScale: 3,
                widthMaxPixels: 3,
                widthMinPixels: 3,
                getColor: outlineColor,
                getWidth: 4,
                capRounded: false,
                billboard: false,
                miterLimit: 0,
            },
            polygons: {
                getFillColor: fillColor,
                getLineColor: outlineColor,
                stroked: true,
                getLineWidth: 3,
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
            },
        },
        extraLayers: [
            {
                provider: "polygons",
                layer: RulerMidpointTextLayer,
            },
            {
                provider: "polygons",
                layer: RulerAreaTextLayer,
            },
        ],
    })
}
